<template>
    <order-summary-layout
        v-if="offer"
        :product-store="productStore"
        :total-price="totalPrice"
        :search-request="searchRequest"
    >
        <template v-slot>
            <div class="text-h6">
                {{ $t('flights.flight_details') }}
            </div>
            <flight-itinerary v-for="(itinerary, index) in offer.itinerary" :key="index" :itinerary="itinerary" />
            <flight-tariff-label :tariff="offer.tariff" class="my-1" />
            <v-dialog v-model="detailModal" max-width="600px" :fullscreen="$breakpoint.smAndDown">
                <template v-slot:activator="{props}">
                    <v-btn variant="text" color="info" size="small" v-bind="props">
                        {{ $t('flights.view_details') }}
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title class="d-flex justify-end">
                        <v-icon @click="detailModal = false">mdi-close</v-icon>
                    </v-card-title>
                    <flight-detail :offer="offer" />
                </v-card>
            </v-dialog>
            <fare-rules :offer-key="offer.offerKey" />
            <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
            <slot name="summary" />
            <flight-conditions-label
                :offer-key="offer.offerKey"
                :price="offer.price"
                :refundable="offer.refundable"
                :regular="offer.regular"
                class="mt-3 text-caption"
                @conditions="showConditionsInfo"
            />
            <div v-if="extraBaggageCount" class="d-flex mt-5">
                <span class="text-caption">{{ $t('flights.extra_baggage') }}</span>
                <strong class="ml-auto pl-2">
                    {{ extraBaggageCount }} {{ $t('flights.baggage_item', extraBaggageCount) }}
                    <template v-if="extraBaggagePrice.amount">({{ $filters.price(extraBaggagePrice) }})</template>
                    <template v-else-if="extraBaggageMandatoryPrice && extraBaggageMandatoryPrice.amount"
                        >({{ $filters.price(extraBaggageMandatoryPrice) }})</template
                    >
                </strong>
            </div>
            <div v-if="seatsCount" class="d-flex mt-5">
                <span class="text-caption">{{ $t('flights.selected_seats') }}</span>
                <strong class="ml-auto pl-2">
                    {{ seatsCount }}
                    {{ $t('flights.seat', seatsCount) }}
                    {{ seats.map(({rowNumber, seatNumber}) => `${rowNumber}${seatNumber}`).join(', ') }}
                    <template v-if="seatsPrice.amount">({{ $filters.price(seatsPrice) }})</template>
                </strong>
            </div>
            <extra-services-summary
                v-if="extraServices && additionalOptions"
                :extra-services="extraServices"
                :additional-options="additionalOptions"
            />
            <conditions-modal>
                <template v-slot:info>
                    <flight-conditions-modal-info />
                </template>
            </conditions-modal>
        </template>
        <template v-slot:violatedPolicies>
            <corporate-policy-violation v-if="corporatePolicyViolated" :policies="violatedPolicies" />
        </template>
    </order-summary-layout>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import FlightItinerary from '@/components/flights/snippets/FlightItinerary.vue'
    import FareRules from '~/components/flights/FareRules.vue'
    import FlightConditionsLabel from '@/components/flights/snippets/FlightConditionsLabel.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import {EventBus, SHOW_FLIGHT_CONDITIONS_INFO} from '@/utils/event-bus'
    import FlightConditionsModalInfo from '@/components/flights/snippets/FlightConditionsModalInfo.vue'
    import FlightDetail from '~/components/flights/search/offers/FlightDetail.vue'
    import CorporatePolicyViolation from '~/components/snippets/CorporatePolicyViolation.vue'
    import OrderSummaryLayout from '@/components/booking/OrderSummaryLayout.vue'
    import {FlightBookingExtraMixin} from '@/components/flights/booking/mixins/FlightBookingExtraMixin.vue'
    import {sumPrice} from '@/utils/api-helpers'
    import {OrderSummaryBase} from '@/components/booking/OrderSummaryBase.vue'
    import ExtraServicesSummary from '@/components/booking/snippets/ExtraServicesSummary.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import FlightTariffLabel from '~/components/flights/search/offers/FlightTariffLabel.vue'

    @Component({
        components: {
            FlightTariffLabel,
            ProductOfferTipsDialog,
            ExtraServicesSummary,
            OrderSummaryLayout,
            FlightConditionsModalInfo,
            ConditionsModal,
            FlightConditionsLabel,
            FlightItinerary,
            FlightDetail,
            FareRules,
            CorporatePolicyViolation,
        },
    })
    export class FlightOrderSummary extends mixins(FlightBookingExtraMixin, OrderSummaryBase) {
        @Prop({required: true}) offer
        @Prop({default: true}) prebooked
        @Prop({required: true}) flightSeatsMap
        @Prop({required: true}) extraBaggageOptions

        detailModal = false

        showConditionsInfo() {
            EventBus.$emit(SHOW_FLIGHT_CONDITIONS_INFO, this.offer)
        }

        get allClasses() {
            const classes = new Set([])
            Object.values(this.offer.tariff).forEach(tarif => {
                tarif.classes.forEach(classType => {
                    classes.add(classType)
                })
            })
            return Array.from(classes).join(' + ')
        }

        get productStore() {
            return flightsStore
        }

        get violatedPolicies() {
            return this.offer.corporatePolicy
        }

        get corporatePolicyViolated() {
            return this.offer.corporatePolicyViolation
        }

        get currency() {
            return persistentStore.getCurrency(this.offer.price.currency)
        }

        get prepareBookRequest() {
            return flightsStore.prepareBookRequest
        }

        get tourists() {
            return persistentStore.tourists
        }

        get orderTotalPrice() {
            const totalPrice = flightsStore.orderTotalPrice(this.offer, this.prebooked, [], this.prepareBookResponse)
            if (this.prebooked) return totalPrice
            return sumPrice([totalPrice, this.extraBaggagePrice, this.seatsPrice])
        }

        get seatsCount() {
            return flightsStore.prepareBookRequest.seats ? flightsStore.prepareBookRequest.seats.length : 0
        }

        get seats() {
            return flightsStore.prepareBookRequest.seats
        }

        get extraBaggageCount() {
            return flightsStore.prepareBookRequest.extraBaggage
                ? flightsStore.prepareBookRequest.extraBaggage.reduce(
                      (count, extraBaggage) => count + extraBaggage.quantity,
                      0
                  )
                : 0
        }
    }

    export default FlightOrderSummary
</script>
